/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, PrimitiveAtom, WritableAtom, atom } from 'jotai';
import {
  BasicSessionInfo,
  LiveStreamInfo,
  OriginType,
  RunType,
  SanctioningBodyEnum,
  SeriesType,
  Event
} from 'models/viewModels';
import { activeEventSelector, activeSessionSelector } from 'jotai/session';
import { liveSessionServiceRSocket } from 'services/stream/RSocket/LiveSessionServiceRSocket';
import { activeSanctioningBodyIdAtom } from 'jotai/sanctioningBody';
import { atomFamily } from 'jotai/utils';
import { sessionService } from 'services/session/SessionService';

let listenerId: string;

const currentLiveStreamAtom = atomFamily<SanctioningBodyEnum, PrimitiveAtom<LiveStreamInfo[]>>(
  (sanctioningBody: SanctioningBodyEnum) => atom<LiveStreamInfo[]>([])
);

const liveStreamInitAtomFamily = atomFamily<SanctioningBodyEnum, WritableAtom<null, [], () => void>>(
  (sanctioningBody: SanctioningBodyEnum) => {
    const newAtom = atom<null, [], () => void>(null, (_get, set) => {
      const activeSanctioningBody = sanctioningBody;
      listenerId = liveSessionServiceRSocket.get(activeSanctioningBody)!.addListener(
        async (liveStreamData) => {
          set(currentLiveStreamAtom(sanctioningBody), liveStreamData);
        },
        async () => {
          set(currentLiveStreamAtom(sanctioningBody), []);
        }
      );
      return () => {
        liveSessionServiceRSocket.get(activeSanctioningBody)?.removeListener(listenerId);
      };
    });
    newAtom.onMount = (init) => {
      init();
    };
    return newAtom;
  }
);

const liveStreamAtomFamily = atomFamily<SanctioningBodyEnum, Atom<LiveStreamInfo[]>>(
  (sanctioningBody: SanctioningBodyEnum) =>
    atom<LiveStreamInfo[]>((get) => {
      get(liveStreamInitAtomFamily(sanctioningBody));
      return get(currentLiveStreamAtom(sanctioningBody));
    })
);

// selector to determine if the livestream base on priority of the series type
// Cup > Xfinity > Truck for NASCAR
// IndyCar > Indy Lights > Indy Pro for IndyCar
export const liveStreamBySanctioningBodyAndSeriesPriorityAtomFamily = atomFamily<
  SanctioningBodyEnum,
  Atom<BasicSessionInfo | undefined>
>((sanctioningBody: SanctioningBodyEnum) =>
  atom<BasicSessionInfo | undefined>((get) => {
    const liveStream = get(liveStreamAtomFamily(sanctioningBody));
    // Array order sets the priority of the series
    let seriesTypes: SeriesType[] = [];
    if (sanctioningBody === SanctioningBodyEnum.NASCAR) {
      seriesTypes = [
        SeriesType[SeriesType.CUP] as unknown as SeriesType,
        SeriesType[SeriesType.XFINITY] as unknown as SeriesType,
        SeriesType[SeriesType.TRUCK] as unknown as SeriesType,
      ];
    } else if (sanctioningBody === SanctioningBodyEnum.INDYCAR) {
      seriesTypes = [
        SeriesType[SeriesType.IndyCar] as unknown as SeriesType,
        SeriesType[SeriesType.IndyLights] as unknown as SeriesType,
        SeriesType[SeriesType.IndyPro] as unknown as SeriesType,
      ];
    }
    for (const seriesType of seriesTypes) {
      if (liveStream.some((item) => item.session.seriesType === seriesType)) {
        // return the first session as seriesTypes is already in priority order
        return returnSessionByOriginPriority(liveStream.filter((item) => item.session.seriesType === seriesType));
      }
    }
  })
);

// helper function to return the session based on the priority of the origin type
// MULTILOOP > RIS > ERDP > RMonitor
const returnSessionByOriginPriority = (liveStream: LiveStreamInfo[]) => {
  return (
    findLiveStreamsession(OriginType.MULTILOOP, liveStream) ||
    findLiveStreamsession(OriginType.RIS, liveStream) ||
    findLiveStreamsession(OriginType.ERDP, liveStream)
    // TODO: Re-enable when RMonitor is on new stack
    // findLiveStreamsession(OriginType.RMONITOR, liveStream)
  );
};

const findLiveStreamsession = (originType: OriginType, liveStream: LiveStreamInfo[]): BasicSessionInfo | undefined => {
  return liveStream.find((item) => item.origins.includes(originType))?.session;
};

/* This selector is used to determine if the active stream matches the
 * session selected in the UI. In the future, we will not start a stream
 * if the it's not active in the backend.
 */
export const isValidSessionSelector = atom<Promise<boolean>>(async (get) => {
  const liveStream = get(liveStreamAtomFamily(get(activeSanctioningBodyIdAtom) ?? SanctioningBodyEnum.NASCAR));
  const activeSession = get(activeSessionSelector);
  const activeEvent = get(activeEventSelector);
  return liveStream.some(
    (item) =>
      item.session.raceId === activeEvent?.eventId &&
      // @ts-ignore
      RunType[item.session.runType] === activeSession?.sessionType
  );
});

export const activeLiveEventSelector = atomFamily<SanctioningBodyEnum, Atom<Promise<Event | undefined>>>(
  (sanctioningBody: SanctioningBodyEnum) =>
    atom<Promise<Event | undefined>>(async (get) => {
      const liveEvent = get(liveStreamBySanctioningBodyAndSeriesPriorityAtomFamily(sanctioningBody));
      const session = liveEvent?.sessionId ? await sessionService.getSession(liveEvent.sessionId) : undefined;

      return session?.event;
    })
);